import React from 'react';
import {
    MotiviewKillerQuestion,
    MotiviewKillerQuestionType,
} from 'typings/target_group';
import {
    MAX_MULTIPLE_CHOICE_OPTIONS,
    MAX_OPTION_TEXT_LENGTH,
    MAX_QUESTION_TEXT_LENGTH,
    MAX_REDIRECT_URL_LENGTH,
    MAX_KILLER_QUESTIONS,
    MIN_MULTIPLE_CHOICE_OPTIONS,
    NO_OPTION_ID,
    YES_OPTION_ID,
    isValidRedirectUrl,
    newOption,
    newQuestion,
    optionalQuestionCount,
    redirectUrlError,
    validateKillerQuestions,
} from 'utils/killerQuestions';
import styles from './KillerQuestionsEditor.module.css';

interface KillerQuestionsEditorProps {
    questions: MotiviewKillerQuestion[];
    onQuestionsChange: (questions: MotiviewKillerQuestion[]) => void;
    minCorrect: number;
    onMinCorrectChange: (minCorrect: number) => void;
    redirectUrl: string;
    onRedirectUrlChange: (url: string) => void;
    /** Set once the recruiter has tried to submit, so errors are not premature. */
    showErrors?: boolean;
}

export const KillerQuestionsEditor = ({
    questions,
    onQuestionsChange,
    minCorrect,
    onMinCorrectChange,
    redirectUrl,
    onRedirectUrlChange,
    showErrors = false,
}: KillerQuestionsEditorProps) => {
    const { questionErrors } = validateKillerQuestions(
        true,
        questions,
        minCorrect,
        redirectUrl,
    );
    const optionalCount = optionalQuestionCount(questions);

    const replaceQuestion = (
        index: number,
        next: MotiviewKillerQuestion,
    ) => {
        const updated = questions.map((question, i) =>
            i === index ? next : question,
        );
        onQuestionsChange(updated);

        // Marking a question as required shrinks the pool the minimum applies
        // to, so a previously valid minimum can fall out of range. Clamp rather
        // than leaving the form in a state the recruiter cannot see the cause of.
        const nextOptional = optionalQuestionCount(updated);
        if (minCorrect > nextOptional) onMinCorrectChange(nextOptional);
    };

    const addQuestion = () => {
        if (questions.length >= MAX_KILLER_QUESTIONS) return;
        onQuestionsChange([...questions, newQuestion()]);
    };

    const removeQuestion = (index: number) => {
        const updated = questions.filter((_, i) => i !== index);
        onQuestionsChange(updated);
        const nextOptional = optionalQuestionCount(updated);
        if (minCorrect > nextOptional) onMinCorrectChange(nextOptional);
    };

    const changeType = (index: number, type: MotiviewKillerQuestionType) => {
        const question = questions[index];
        if (question.type === type) return;

        replaceQuestion(
            index,
            type === 'YES_NO'
                ? {
                      ...question,
                      type,
                      options: undefined,
                      correct_option_ids: [YES_OPTION_ID],
                  }
                : {
                      ...question,
                      type,
                      options: [newOption(), newOption()],
                      correct_option_ids: [],
                  },
        );
    };

    const toggleCorrectOption = (index: number, optionId: string) => {
        const question = questions[index];
        const isCorrect = question.correct_option_ids.includes(optionId);
        replaceQuestion(index, {
            ...question,
            correct_option_ids: isCorrect
                ? question.correct_option_ids.filter((id) => id !== optionId)
                : [...question.correct_option_ids, optionId],
        });
    };

    const addOption = (index: number) => {
        const question = questions[index];
        const options = question.options ?? [];
        if (options.length >= MAX_MULTIPLE_CHOICE_OPTIONS) return;
        replaceQuestion(index, {
            ...question,
            options: [...options, newOption()],
        });
    };

    const removeOption = (index: number, optionId: string) => {
        const question = questions[index];
        const options = (question.options ?? []).filter(
            (option) => option.id !== optionId,
        );
        replaceQuestion(index, {
            ...question,
            options,
            correct_option_ids: question.correct_option_ids.filter(
                (id) => id !== optionId,
            ),
        });
    };

    const redirectInvalid = showErrors && !isValidRedirectUrl(redirectUrl);

    // Only once the field is nearly full: a maxLength that silently stops
    // accepting characters reads as a broken field without it.
    const characterCounter = (value: string, max: number) => {
        if (value.length < max * 0.8) return null;
        return (
            <span
                className={`${styles.counter} ${
                    value.length >= max ? styles.counterFull : ''
                }`}
            >
                {value.length}/{max}
            </span>
        );
    };

    return (
        <div className={styles.editor}>
            <p className={styles.intro}>
                Candidates answer these questions before the video. They are
                asked in the order below, and judged once at the end.
            </p>

            <div className={styles.count}>
                <strong>{questions.length}</strong> of {MAX_KILLER_QUESTIONS}{' '}
                questions added
            </div>

            {questions.map((question, index) => {
                const error = showErrors
                    ? questionErrors[question.id]
                    : undefined;
                const options = question.options ?? [];

                return (
                    <div key={question.id} className={styles.question}>
                        <div className={styles.questionHeader}>
                            <span className={styles.number}>{index + 1}</span>
                            <input
                                type="text"
                                className={`${styles.text} ${
                                    error ? styles.invalid : ''
                                }`}
                                value={question.text}
                                placeholder="Question"
                                maxLength={MAX_QUESTION_TEXT_LENGTH}
                                onChange={(e) =>
                                    replaceQuestion(index, {
                                        ...question,
                                        text: e.target.value,
                                    })
                                }
                            />
                            {characterCounter(
                                question.text,
                                MAX_QUESTION_TEXT_LENGTH,
                            )}
                            <button
                                type="button"
                                className={styles.remove}
                                onClick={() => removeQuestion(index)}
                                aria-label={`Remove question ${index + 1}`}
                            >
                                Remove
                            </button>
                        </div>

                        <div className={styles.controls}>
                            <label className={styles.control}>
                                <span className={styles.controlLabel}>
                                    Answer format
                                </span>
                                <select
                                    className={styles.select}
                                    value={question.type}
                                    onChange={(e) =>
                                        changeType(
                                            index,
                                            e.target
                                                .value as MotiviewKillerQuestionType,
                                        )
                                    }
                                >
                                    <option value="YES_NO">Yes / No</option>
                                    <option value="MULTIPLE_CHOICE">
                                        Multiple choice
                                    </option>
                                </select>
                            </label>

                            {question.type === 'YES_NO' && (
                                <label className={styles.control}>
                                    <span className={styles.controlLabel}>
                                        Correct answer
                                    </span>
                                    <select
                                        className={styles.select}
                                        value={
                                            question.correct_option_ids[0] ??
                                            YES_OPTION_ID
                                        }
                                        onChange={(e) =>
                                            replaceQuestion(index, {
                                                ...question,
                                                correct_option_ids: [
                                                    e.target.value,
                                                ],
                                            })
                                        }
                                    >
                                        <option value={YES_OPTION_ID}>
                                            Yes
                                        </option>
                                        <option value={NO_OPTION_ID}>No</option>
                                    </select>
                                </label>
                            )}

                            <label className={styles.mustRow}>
                                <input
                                    type="checkbox"
                                    checked={question.must_be_correct}
                                    onChange={(e) =>
                                        replaceQuestion(index, {
                                            ...question,
                                            must_be_correct: e.target.checked,
                                        })
                                    }
                                />
                                <span>
                                    Must be answered correctly
                                    <em className={styles.mustHint}>
                                        A wrong answer rejects the candidate,
                                        whatever else they answer.
                                    </em>
                                </span>
                            </label>
                        </div>

                        {question.type === 'MULTIPLE_CHOICE' && (
                            <div className={styles.options}>
                                <span className={styles.controlLabel}>
                                    Answer options, tick every correct one
                                </span>
                                {options.map((option) => (
                                    <div
                                        key={option.id}
                                        className={styles.option}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={question.correct_option_ids.includes(
                                                option.id,
                                            )}
                                            onChange={() =>
                                                toggleCorrectOption(
                                                    index,
                                                    option.id,
                                                )
                                            }
                                            aria-label="Correct answer"
                                        />
                                        <input
                                            type="text"
                                            className={styles.optionText}
                                            value={option.text}
                                            placeholder="Answer option"
                                            maxLength={MAX_OPTION_TEXT_LENGTH}
                                            onChange={(e) =>
                                                replaceQuestion(index, {
                                                    ...question,
                                                    options: options.map((o) =>
                                                        o.id === option.id
                                                            ? {
                                                                  ...o,
                                                                  text: e.target
                                                                      .value,
                                                              }
                                                            : o,
                                                    ),
                                                })
                                            }
                                        />
                                        {characterCounter(
                                            option.text,
                                            MAX_OPTION_TEXT_LENGTH,
                                        )}
                                        <button
                                            type="button"
                                            className={styles.removeOption}
                                            disabled={
                                                options.length <=
                                                MIN_MULTIPLE_CHOICE_OPTIONS
                                            }
                                            onClick={() =>
                                                removeOption(index, option.id)
                                            }
                                            aria-label="Remove option"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className={styles.addOption}
                                    disabled={
                                        options.length >=
                                        MAX_MULTIPLE_CHOICE_OPTIONS
                                    }
                                    onClick={() => addOption(index)}
                                >
                                    + Add option
                                </button>
                            </div>
                        )}

                        {error && <div className={styles.error}>{error}</div>}
                    </div>
                );
            })}

            <button
                type="button"
                className={styles.addQuestion}
                disabled={questions.length >= MAX_KILLER_QUESTIONS}
                onClick={addQuestion}
            >
                + Add question
            </button>

            <div className={styles.rule} />

            {/*
              The minimum applies only to the questions that are not already
              required. Counting the required ones would mean anyone who cleared
              that bar carries free credit into this one.
            */}
            {optionalCount > 0 ? (
                <label className={styles.minRow}>
                    <span>
                        Of the {optionalCount} question
                        {optionalCount === 1 ? '' : 's'} not marked as required,
                        at least
                    </span>
                    <select
                        className={styles.select}
                        value={Math.min(minCorrect, optionalCount)}
                        onChange={(e) =>
                            onMinCorrectChange(Number(e.target.value))
                        }
                    >
                        {Array.from({ length: optionalCount + 1 }, (_, i) => (
                            <option key={i} value={i}>
                                {i}
                            </option>
                        ))}
                    </select>
                    <span>must be correct.</span>
                </label>
            ) : (
                questions.length > 0 && (
                    <p className={styles.allRequired}>
                        Every question is marked as required, so all of them
                        must be answered correctly.
                    </p>
                )
            )}

            <label className={styles.redirectRow}>
                <span className={styles.controlLabel}>
                    Send rejected candidates to this web address
                </span>
                <input
                    type="url"
                    className={`${styles.redirect} ${
                        redirectInvalid ? styles.invalid : ''
                    }`}
                    value={redirectUrl}
                    placeholder="https://example.com/careers"
                    maxLength={MAX_REDIRECT_URL_LENGTH}
                    onChange={(e) => onRedirectUrlChange(e.target.value)}
                />
                {redirectInvalid && (
                    <span className={styles.error}>
                        {redirectUrlError(redirectUrl)}
                    </span>
                )}
            </label>
        </div>
    );
};
